import { useState, useEffect } from 'react';
import { getOrganisations, getOrganisationProfiles, getFeatures, getProfileFeatures, updateProfileFeatures } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import EmptyState from '../components/EmptyState';

const ACCESS_LEVELS = ['', 'READ', 'WRITE', 'DELETE'];
const ACCESS_LABELS = { '': 'No Access', READ: 'Read', WRITE: 'Read + Write', DELETE: 'Full Access' };

export default function ProfilePermissionsPage() {
  const { isAdminForOrg } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [features, setFeatures] = useState([]);
  const [profileFeatures, setProfileFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getOrganisations().then(setOrgs).catch(() => {});
    getFeatures().then(setFeatures).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedOrgId) { setProfiles([]); setSelectedProfileId(''); return; }
    getOrganisationProfiles(selectedOrgId).then((p) => {
      setProfiles(p);
      setSelectedProfileId('');
    }).catch(() => setProfiles([]));
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedOrgId || !selectedProfileId) { setProfileFeatures([]); return; }
    setLoading(true);
    getProfileFeatures(selectedOrgId, selectedProfileId)
      .then(setProfileFeatures)
      .catch(() => setProfileFeatures([]))
      .finally(() => setLoading(false));
  }, [selectedOrgId, selectedProfileId]);

  const getAccessLevel = (featureId) => {
    const pf = profileFeatures.find((p) => p.featureId === featureId);
    return pf ? pf.accessLevel : '';
  };

  const handleAccessChange = (featureId, level) => {
    setProfileFeatures((prev) => {
      const existing = prev.find((p) => p.featureId === featureId);
      if (level === '') {
        return prev.filter((p) => p.featureId !== featureId);
      }
      if (existing) {
        return prev.map((p) => p.featureId === featureId ? { ...p, accessLevel: level } : p);
      }
      const feature = features.find((f) => f.id === featureId);
      return [...prev, { featureId, featureCode: feature?.code, featureName: feature?.name, accessLevel: level }];
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const featureAssignments = features.map((f) => ({
        featureId: f.id,
        accessLevel: getAccessLevel(f.id) || null,
      }));
      const result = await updateProfileFeatures(selectedOrgId, selectedProfileId, featureAssignments);
      setProfileFeatures(result);
      setSuccess('Permissions saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const adminOrgs = orgs.filter((o) => isAdminForOrg(o.id));

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Permissions' },
        ]} />
        <h2>Profile Permissions</h2>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="form-row">
          <div className="form-group">
            <label>Organisation</label>
            <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
              <option value="">Select organisation...</option>
              {adminOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Profile</label>
            <select value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)} disabled={!selectedOrgId}>
              <option value="">Select profile...</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {loading && <p>Loading permissions...</p>}

        {selectedProfileId && !loading && features.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Description</th>
                    <th style={{ minWidth: 160 }}>Access Level</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((f) => (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 600 }}>{f.name}</td>
                      <td style={{ fontSize: '0.85rem', color: '#666' }}>{f.description}</td>
                      <td>
                        <select
                          value={getAccessLevel(f.id)}
                          onChange={(e) => handleAccessChange(f.id, e.target.value)}
                          style={{ width: '100%', padding: '0.4rem' }}
                        >
                          {ACCESS_LEVELS.map((lvl) => (
                            <option key={lvl} value={lvl}>{ACCESS_LABELS[lvl]}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </>
        )}

        {selectedProfileId && !loading && features.length === 0 && (
          <EmptyState message="No features defined yet." />
        )}
      </div>
    </div>
  );
}
